import React, { type FC, useLayoutEffect, useRef, useState } from 'react'

import clsx from 'clsx'

import type {
    ApplyV2FileAttachment,
    ApplyV2TempBase64File,
    ApplyV2TempCourseFile,
    V2CourseFile,
} from '@core/app/types/applyV2Type'
import FileUploaderV2ListItem from '@core/components/ApplyDialogV2/FileUploaderV2ListItem'

import './FileUploaderV2List.scss'

const baseClass = 'FileUploaderV2List'

interface FileUploaderV2ListProps {
    files: (File | ApplyV2TempCourseFile | ApplyV2FileAttachment)[]
    fileErrors?: { file: File; error: string }[]
    onDelete?: (
        file: File | ApplyV2TempCourseFile | ApplyV2FileAttachment | V2CourseFile | ApplyV2TempBase64File
    ) => void | Promise<void>
    oldCv?: boolean
    isShowDelete?: boolean
    isShowParseInfo?: boolean
    className?: string
    loadingId?: string | number | null
    loadingIds?: (string | number)[]
    loadingCount?: number
    onFileClick?: (
        file: File | ApplyV2TempCourseFile | ApplyV2FileAttachment | V2CourseFile | ApplyV2TempBase64File
    ) => void
}

const FileUploaderV2List: FC<FileUploaderV2ListProps> = ({
    fileErrors = [],
    files = [],
    onDelete,
    oldCv = false,
    isShowDelete = true,
    isShowParseInfo = true,
    className,
    loadingId = 0,
    loadingCount = 0,
    loadingIds = [],
    onFileClick,
}) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [isNarrow, setIsNarrow] = useState(false)

    useLayoutEffect(() => {
        const checkWidth = () => {
            if (containerRef.current) {
                setIsNarrow(containerRef.current.offsetWidth < 500)
            }
        }

        checkWidth()
        window.addEventListener('resize', checkWidth)

        return () => {
            window.removeEventListener('resize', checkWidth)
        }
    }, [])

    const getFileId = (file: File | ApplyV2TempCourseFile | ApplyV2FileAttachment): string => {
        return 'uid' in file ? file.uid : file.name
    }

    const handleFileClick = (
        file: File | ApplyV2TempCourseFile | ApplyV2FileAttachment | V2CourseFile | ApplyV2TempBase64File
    ) => {
        if (onFileClick) {
            onFileClick(file)
            return
        }
        const downloadUrl =
            'downloadUrl' in file && typeof file.downloadUrl === 'string' && file.downloadUrl
                ? file.downloadUrl
                : 'downloadLink' in file && typeof file.downloadLink === 'string' && file.downloadLink
                  ? file.downloadLink
                  : ''

        if (!downloadUrl) return

        window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    }

    return (
        <div ref={containerRef} className={clsx(baseClass, className)}>
            {(Array.isArray(files) ? files : []).map((file, index) => {
                const error = fileErrors.find((err) => err.file === file)?.error
                const isLast = index === files.length + loadingCount - 1
                const isOdd = (files.length + loadingCount) % 2 === 1
                const classNameItem = clsx(`${baseClass}__item`, {
                    [`${baseClass}--narrow`]: isNarrow,
                    [`${className}--narrow`]: isNarrow && className,
                    [`${baseClass}__item--full`]: isLast && isOdd,
                    [`${baseClass}__item--last`]: isLast,
                    [`${baseClass}__item--error`]: error,
                    [`${baseClass}__item--old-cv`]: oldCv,
                    [`${baseClass}__item--show-delete`]: isShowDelete,
                    [`${baseClass}__item--show-parse-info`]: isShowParseInfo,
                    [`${className}__item`]: className,
                    [`${className}__item--full`]: isLast && isOdd && className,
                    [`${className}__item--last`]: isLast && className,
                    [`${className}__item--error`]: error && className,
                    [`${className}__item--old-cv`]: oldCv && className,
                    [`${className}__item--show-delete`]: isShowDelete && className,
                    [`${className}__item--show-parse-info`]: isShowParseInfo && className,
                })
                return (
                    <React.Fragment key={index}>
                        <FileUploaderV2ListItem
                            file={file}
                            fileError={error}
                            onDelete={onDelete}
                            oldCv={oldCv}
                            className={classNameItem}
                            isShowDelete={isShowDelete}
                            loading={('uid' in file && loadingId === file?.uid) || loadingIds.includes(getFileId(file))}
                            onClick={handleFileClick}
                        />
                        {!error && !oldCv && isShowParseInfo && (
                            <div className={`${baseClass}__cv-parse-info`}>
                                <div className={`${baseClass}__cv-parse-title`}>
                                    Would you like to automatically parse your CV?
                                </div>
                                <div className={`${baseClass}__cv-parse-desc`}>
                                    Information from your CV file can be automatically parsed to fill in the details in
                                    the following steps (your profile as well). You will be able to add or edit any
                                    information at any time.
                                </div>
                                <div className={`${baseClass}__cv-parse-radio-group`}>
                                    <label className={`${baseClass}__cv-parse-radio`}>
                                        <input type="radio" name="cv-parse" value="yes" defaultChecked /> Yes
                                    </label>
                                    <label className={`${baseClass}__cv-parse-radio`}>
                                        <input type="radio" name="cv-parse" value="no" /> No, I'll add information
                                        myself
                                    </label>
                                </div>
                            </div>
                        )}
                    </React.Fragment>
                )
            })}
            {Array.from({ length: loadingCount }).map((_, index) => {
                const isLast = index === files.length + loadingCount - 1
                const isOdd = (files.length + loadingCount) % 2 === 1
                return (
                    <FileUploaderV2ListItem
                        key={`loading-${index}`}
                        file={{ name: '', size: 0, type: '', uid: `loading-${index}` } as unknown as File}
                        loading={true}
                        className={clsx(`${baseClass}__item ${baseClass}__item--loading`, {
                            [`${baseClass}__item--full`]: isLast && isOdd,
                            [`${baseClass}__item--last`]: isLast,
                            [`${className}__item`]: className,
                            [`${className}__item--full`]: isLast && isOdd && className,
                            [`${className}__item--last`]: isLast && className,
                        })}
                    />
                )
            })}
        </div>
    )
}

export default FileUploaderV2List
